import React, { useState, useEffect } from 'react';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import styled from '@emotion/styled';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { getSlidebarTheme, getTextColor, getButtonTheme, getSideBarNav, getBorders } from '../../../../style';
import { removeBodyScroll, isDarkMode } from '../../../../utils/helpers';
import { useApp } from '../../../../contexts/AppContext';
import Button from '../../../../atoms/buttons/Button';
import ArrowLeft from '../../../../atoms/icons/ArrowLeft';
import { handlePhoneSettingsVysionEvent } from '../../../../utils/vysion/phoneSettings';
import { VYSION_ACTIONS } from '../../../../utils/vysion';
import { PhoneSettingsClickVysionEventsEnum } from '../../../../utils/vysion/phoneSettings/click';
import { PhoneSettingsViewVysionEventsEnum } from '../../../../utils/vysion/phoneSettings/view';
import { handlePaymentVysionEvent } from '../../../../utils/vysion/payment';
import { PaymentClickVysionEventsEnum } from '../../../../utils/vysion/payment/click';
import { PaymentViewVysionEventsEnum } from '../../../../utils/vysion/payment/view';
import { Typography } from '@material-ui/core';
import { mixpanelTrackEvent } from '../../../../utils/mixpanel/common';
import { EVENT_NAMES } from '../../../../constants/mixpanel';
import { isMobile } from 'react-device-detect';
import { FONT_FAMILIES } from 'theme/material/constants';

const SidebarFooter = styled('div')({
  padding: '45px 25px 45px 25px',
  minHeight: 40,
});

const StyledSlidebar = styled('div')(() => ({
  background: `${getSideBarNav().bgGradient} !important`,

  'ul li, span': {
    ...getTextColor().DARK_2,
  },

  'ul li:hover': {
    backgroundColor: getSlidebarTheme().listHover,
  },
}));

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const BackArrowContainer = styled('div')(() => ({
  position: 'fixed',
  top: 19,
  borderRadius: '50%',
  width: '36px',
  height: '36px',
  boxShadow: '0px 2px 4px 0px #00000029',
  background: isDarkMode() ? '#000000' : '#F6F4F4',
}));

const SidebarTitle = styled('div')(() => ({
  padding: '25px 0',
  margin: 0,
  '> span': { ...getTextColor().DARK_2 },
}));

const handleAccountTabScrollLock = hidden => {
  const element = document.getElementById(`child-sidebar1-Account Settings`);

  if (element) {
    element.style.overflowY = hidden ? 'hidden' : 'scroll';
    element.scrollTop = 0;
  }
};

const tabDataChildren = (tabData, activeTab, activeTabName, setActiveTab, setActiveTabName, isMobile) => {
  if (tabData.children) {
    document.getElementById('sidebar-parent').scrollTop = 0;
    const data = [...activeTab];
    const dataName = [...activeTabName];

    data.push(tabData.id);
    dataName.push(tabData.name);
    setActiveTab(data);
    setActiveTabName(dataName);
    document.getElementById('sidebar-header').scrollIntoView({ behavior: 'smooth' });
    enableBodyScroll(document.getElementById('sidebar-parent'));
    disableBodyScroll(document.getElementById(`child-sidebar1-${tabData.name}`));
    document.getElementById('sidebar-parent').style.overflowY = 'hidden';
    setCustomSideBarMainContentStyle(false, isMobile);

    if (tabData.id === 30) {
      handleAccountTabScrollLock(false);
      setCustomSideBarMainContentStyle(true, isMobile);
    }
  }
};

const setCustomSideBarMainContentStyle = (addPadding: boolean, isMobile: boolean) => {
  const smcElem = document.getElementById('sidebar-main-content-account-settings');

  if (!smcElem) {
    return;
  }

  if (!isMobile) {
    return;
  }

  smcElem.style.paddingBottom = addPadding ? '200px' : '0';
};

const setActiveTabHeight = (activeTab, activeTabName) => {
  if (activeTab.length > 0) {
    const client = document.getElementById(`child-sidebar1-${activeTabName[0]}`);

    client.style.minHeight = '';
  }
};

const handleBackClickBehaviour = (
  tabData,
  activeTab,
  activeTabName,
  setActiveTab,
  setActiveTabName,
  resetTabs,
  isMobile
) => {
  if (tabData) {
    const data = [...activeTab];
    const dataName = [...activeTabName];
    const index = data.findIndex(id => id === tabData.id);
    const indexforName = dataName.findIndex(name => name === tabData.name);

    data.splice(index, 1);
    dataName.splice(indexforName, 1);
    setActiveTab(data);
    setActiveTabName(dataName);
    handleAccountTabScrollLock(false);
    if (activeTab.length === 1) {
      document.getElementById('sidebar-parent').style.overflowY = 'scroll';
    }
    disableBodyScroll(document.getElementById('sidebar-parent'));
    setCustomSideBarMainContentStyle(false, isMobile);
  } else {
    resetTabs();
  }
};

const MultilevelSidebar = (props: any): any => {
  const { open, wrapperClassName, headerClassName, header, options, width, hasNotify, onToggleHide } = props;
  const [activeTab, setActiveTab] = useState([]);
  const [activeTabName, setActiveTabName] = useState([]);
  const {
    setCurrentPhoneSettingsStep,
    setHasRequestOtpError,
    setShowOtpCountdown,
    forceCloseSliderMenu,
    setPaymentSettingsOpen,
    forceResetTabs,
  } = useApp();
  const location = useLocation();

  const resetTabs = (shouldDisableBodyScroll = true) => {
    const childSidebar = document.getElementById(`child-sidebar1-${activeTabName[0]}`);

    childSidebar && enableBodyScroll(childSidebar);

    if (shouldDisableBodyScroll) {
      disableBodyScroll(document.getElementById('sidebar-parent'));
    }

    document.getElementById('sidebar-parent').style.overflowY = 'scroll';
    handleAccountTabScrollLock(false);
    setActiveTab([]);
    setActiveTabName([]);
    setCustomSideBarMainContentStyle(false, props.isMobile);
  };

  useEffect(() => {
    onToggleHide();
    removeBodyScroll();

    if (Array.isArray(activeTab) && activeTab.length > 0) {
      resetTabs(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    hasNotify &&
      setTimeout(() => {
        setActiveTab([]);
        setActiveTabName([]);
        clearAllBodyScrollLocks();
        document.getElementById('sidebar-parent').style.overflowY = 'scroll';
        handleAccountTabScrollLock(false);
      }, 500);
  }, [hasNotify]);

  useEffect(() => {
    setTimeout(() => {
      setActiveTabHeight(activeTab, activeTabName);
    }, 500);
  }, [activeTab, activeTabName]);

  const resetChangePhoneSettings = () => {
    // used for back drop and back button
    setCurrentPhoneSettingsStep(1);
    setHasRequestOtpError('');
    setHasRequestOtpError('');
    setShowOtpCountdown(false);
  };

  const handleBackdropClick = () => {
    const { onToggle, onClose } = props;

    onToggle(false);
    onClose && onClose();
    setCustomSideBarMainContentStyle(false, props.isMobile);
    removeBodyScroll();
    resetTabs();

    resetChangePhoneSettings();
    setPaymentSettingsOpen(false);
  };

  useEffect(() => {
    // listen to trigger close the slider menu inside the slider menu content
    if (forceCloseSliderMenu > 0) {
      handleBackdropClick();
    }
  }, [forceCloseSliderMenu]);

  useEffect(() => {
    // listen to trigger close the slider menu inside the slider menu content
    if (forceResetTabs > 0) {
      resetTabs();
    }
  }, [forceResetTabs]);

  const handleVysionTabClickEvent = (tabName: string) => {
    switch (tabName) {
      case 'Account Settings':
        handlePhoneSettingsVysionEvent({
          action: VYSION_ACTIONS.CLICK,
          event: PhoneSettingsClickVysionEventsEnum.CLICK_ACCOUNT_MENU_SETTINGS,
        });
        break;
      case 'Change Phone Number':
        handlePhoneSettingsVysionEvent({
          action: VYSION_ACTIONS.CLICK,
          event: PhoneSettingsClickVysionEventsEnum.CLICK_ACCOUNT_MENU_CHANGE_PHONE_NUMBER,
        });
        handlePhoneSettingsVysionEvent({
          action: VYSION_ACTIONS.VIEW,
          event: PhoneSettingsViewVysionEventsEnum.VIEW_ACCOUNT_CHANGE_PHONE_NUMBER,
        });
        break;
      case 'Payment Settings':
        handlePaymentVysionEvent({
          action: VYSION_ACTIONS.CLICK,
          event: PaymentClickVysionEventsEnum.CLICK_ACCOUNT_MENU_PAYMENT_SETTINGS,
        });
        handlePaymentVysionEvent({
          action: VYSION_ACTIONS.VIEW,
          event: PaymentViewVysionEventsEnum.VIEW_ACCOUNT_MENU_PAYMENT_SETTINGS,
        });
        break;
      case 'Deactivate or Delete My Account':
        localStorage.removeItem('came_from_delete_page');

        mixpanelTrackEvent({
          eventName: EVENT_NAMES.DEACTIVATE_OR_DELETE_MY_ACCOUNT_CLICKED,
          payload: {},
        });

        break;
    }
  };

  const handleTabClick = tabData => {
    const { onItemClick } = props;

    if (tabData.disabled) {
      return;
    }

    if (tabData.id === 42) {
      setPaymentSettingsOpen(true);
    }

    handleVysionTabClickEvent(tabData?.name ?? '');
    tabDataChildren(tabData, activeTab, activeTabName, setActiveTab, setActiveTabName, props.isMobile);

    if (tabData.link) {
      resetTabs();
    }
    onItemClick && onItemClick(tabData);
  };

  const handleBackClick = tabData => {
    const { onBackClick } = props;

    handleBackClickBehaviour(
      tabData,
      activeTab,
      activeTabName,
      setActiveTab,
      setActiveTabName,
      resetTabs,
      props.isMobile
    );
    onBackClick && onBackClick(activeTab);

    resetChangePhoneSettings();

    if (tabData.id === 42) {
      setPaymentSettingsOpen(false);
    }
  };

  const getParentHeight = () => {
    if (activeTab.length > 0) {
      // Privacy Preferences
      if (activeTab.includes(27)) {
        return '1150px';
      }

      // Account Settings
      if (activeTab.includes(30)) {
        return '1080px';
      }

      return '900px';
    }
    return '100vh';
  };

  const renderBackBtn = (handleClick, dataTestId, list) => {
    return (
      <BackArrowContainer style={isMobile ? { right: 22 } : { left: 22 }}>
        <Button
          customStyle={{
            padding: 15,
            height: '36px',
            width: '36px',
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            ':active': {
              borderRadius: '20px !important',
              background:
                (isDarkMode() ? '#212121' : 'linear-gradient(180deg, #E4F8F6 0%, #FFFFFF 100%)!important') +
                ' !important',
              float: 'inline-start',
            },
          }}
          buttonType="link"
          onClick={() => handleClick(list)}
          data-test-id={dataTestId}
        >
          <ArrowLeft
            color={isDarkMode() ? '#FFFFFF' : '#0A5B91'}
            cssStyle={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: isMobile ? 'translate(-50%, -50%)' : 'translate(-50%, -50%) rotateZ(180deg)',
              transformStyle: 'preserve-3d',
            }}
          />
        </Button>
      </BackArrowContainer>
    );
  };

  const renderSecondChildren = (parent, list) => {
    return (
      <SidebarContent
        {...props}
        sidebarProps={{
          id: `child-sidebar2-${list.name}`,
          key: list.key || list.id,
          className: classNames('sidebar-main second', {
            show: activeTab.includes(list.id),
            [wrapperClassName]: wrapperClassName,
          }),
          style: { minHeight: getParentHeight(), width, overflow: 'hidden', background: getSideBarNav().bgGradient },
        }}
        headerContent={
          <SidebarTitle id={`subHeader_${list.id}`} className="second-back-btn" style={{ borderBottom: 'none' }}>
            {renderBackBtn(handleBackClick, 'second-back-btn', list)}
            {/* @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389 */}
            <Typography
              variant="body1"
              minFontSize={20}
              style={{
                fontFamily: FONT_FAMILIES.secondary,
                fontSize: 24,
                fontWeight: 400,
                lineHeight: '18px',
                letterSpacing: -0.3,
                color: isDarkMode() ? '#F6F4F4' : '#212121',
              }}
            >
              {list.title || list.name}
            </Typography>
          </SidebarTitle>
        }
        options={list.children}
        handleTabClick={handleTabClick}
      />
    );
  };

  const renderFirstChildren = list => {
    return (
      <SidebarContent
        {...props}
        sidebarProps={{
          id: `child-sidebar1-${list.name}`,
          key: list.key || null,
          className: classNames('sidebar-main second', {
            show: activeTab.includes(list.id),
            [wrapperClassName]: wrapperClassName,
          }),
          style: {
            overflowY: activeTab.includes(41) ? 'hidden' : 'scroll',
            minHeight: getParentHeight(),
            width: width,
            background: getSideBarNav().bgGradient,
          },
        }}
        headerContent={
          <SidebarTitle id={`subHeader_${list.id}`} className="first-back-btn">
            {renderBackBtn(handleBackClick, 'first-back-btn', list)}
            {/* @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389 */}
            <Typography
              variant="body1"
              minFontSize={20}
              style={{
                fontFamily: FONT_FAMILIES.secondary,
                fontSize: 24,
                fontWeight: 400,
                lineHeight: '18px',
                letterSpacing: -0.3,
                color: isDarkMode() ? '#F6F4F4' : '#212121',
              }}
            >
              {list.name}
            </Typography>
          </SidebarTitle>
        }
        options={list.children}
        handleTabClick={handleTabClick}
      >
        {data => data.children && renderSecondChildren(list, data)}
      </SidebarContent>
    );
  };

  return (
    <div id="react-sidebar" className="slidebar">
      <div className={classNames('sidebar-backdrop', { show: open })} onClick={handleBackdropClick} />
      <SidebarContent
        {...props}
        sidebarProps={{
          id: 'sidebar-parent',
          className: classNames(`sidebar-main ${props.isMobile ? 'mobile' : 'desktop'}`, {
            show: open,
            [wrapperClassName]: wrapperClassName,
          }),
          style: { width: width, background: getSideBarNav().bgGradient },
        }}
        headerContent={
          typeof header === 'string' ? (
            <div
              className={`sidebar-header ${classNames({
                [headerClassName]: headerClassName,
              })}`}
            >
              {header}
            </div>
          ) : (
            <div
              className={classNames({
                [headerClassName]: headerClassName,
              })}
            >
              {header}
            </div>
          )
        }
        options={options}
        handleTabClick={handleTabClick}
      >
        {list => list.children && renderFirstChildren(list)}
      </SidebarContent>
    </div>
  );
};

export default MultilevelSidebar;

const SidebarContent = props => {
  const { sidebarProps, headerContent, options, children, handleTabClick } = props;

  const parentMenu = (id, hasSecondChild = false) => {
    return identifyParent(id) && !hasSecondChild
      ? { fontSize: 16, lineHeight: '21px' }
      : { maxWidth: props.isMobile ? 260 : 320, paddingLeft: 20, fontSize: 16, lineHeight: '21px' };
  };

  // create a method to determine which menu links are parent ones
  const identifyParent = id => {
    const { parentMenuItems } = props;

    for (const item of parentMenuItems) {
      if (id === item) {
        return true;
      }
    }
    return false;
  };

  const renderParentLink = list => {
    return (
      <li
        id={identifyParent(list.id) ? `${list.name}-Section` : `${list.name}`}
        className={classNames({
          disabled: list.disabled,
        })}
        onClick={() => handleTabClick(list)}
      >
        {list.name !== '' && (
          <span className="flex-align-center">
            <Typography
              variant="body2"
              style={{
                ...parentMenu(list.id),
                ...(list.to === '/logout'
                  ? {
                      color: isDarkMode() ? '#FF3A67' : '#E21D4A',
                      fontSize: 18,
                      fontWeight: 600,
                      lineHeight: '23px',
                      letterSpacing: -0.5,
                    }
                  : {}),
              }}
            >
              {list.name}
            </Typography>
          </span>
        )}
        {(children && list.children && list.icon) || list.icon}
      </li>
    );
  };

  const renderlink = (fullurl, list) => {
    if (fullurl) {
      return (
        <a data-test-id={`cypress-${list.name}-link`} href={list.to} target="_blank" rel="noreferrer">
          {renderParentLink(list)}
        </a>
      );
    } else {
      return (
        <Link to={list.to} replace={list?.replace || false}>
          {renderParentLink(list)}
        </Link>
      );
    }
  };

  const renderList = (isParent, list) => {
    return isParent ? `${list.name}-Section` : `${list.name}`;
  };

  const titleIcon = data => {
    return data.titleIcon ? data.titleIcon : '';
  };

  const navList = (list, index) => {
    const withListProperties = Object.keys(list);

    return (
      withListProperties.length > 0 && (
        <div key={index}>
          {list.to && !list.children && !list.disabled ? (
            renderlink(list.fullurl, list)
          ) : (
            <li
              id={renderList(identifyParent(list.id), list)}
              className={classNames({ disabled: list.disabled })}
              onClick={() => handleTabClick(list)}
            >
              {!list.noLabel && list.name !== '' && (
                <span className="flex-align-center">
                  <Typography variant="body2" style={parentMenu(list.id, list?.hasSecondChild ?? false)}>
                    {list.name}
                  </Typography>
                </span>
              )}
              {(children && list.children && list.icon) || list.icon}
            </li>
          )}
          {children && children(list)}
          {list.id === 30 ? (
            <hr
              className="section-seprator"
              style={{
                margin: '0',
                borderTop: getBorders().PRIMARY_DASHED,
                backgroundColor: 'transparent',
              }}
            />
          ) : null}
        </div>
      )
    );
  };

  return (
    <StyledSlidebar {...sidebarProps}>
      <div
        className={`sidebar-main-content ${isDarkMode() ? 'darkMode' : 'lightMode'}`}
        id={`sidebar-main-content-${sidebarProps.key || sidebarProps.id}`}
      >
        {headerContent}
        <div className="sidebar-body" style={{ padding: '0 25px 0 25px' }}>
          {options.map((data, idx) => {
            return (
              <div key={idx}>
                {!(!!data.hideBorder || idx === 0) && (
                  <hr
                    className="section-seprator"
                    style={{
                      margin: '0',
                      borderTop: getBorders().PRIMARY_DASHED,
                      backgroundColor: 'transparent',
                    }}
                  />
                )}
                {data.title && (
                  <div className="section-heading">
                    {titleIcon(data)}
                    <span
                      className={classNames({ text: data.titleIcon })}
                      style={{ color: getButtonTheme().sideBarHeader }}
                    >
                      {data.title}
                    </span>
                  </div>
                )}
                {data?.isCustomComponent ? (
                  <div>{data.content}</div>
                ) : (
                  <ul>
                    {data.content.length > 0 ? (
                      data.content.map((list, index) => {
                        {
                          if (!list.hidden) {
                            return navList(list, index);
                          } else {
                            return <div key={index}></div>;
                          }
                        }
                      })
                    ) : (
                      <div style={{ padding: '1.3rem 1.5rem 1.3rem 1.5rem' }} />
                    )}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <SidebarFooter />
    </StyledSlidebar>
  );
};

MultilevelSidebar.propTypes = {
  open: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.node]),
  persist: PropTypes.bool,
  wrapperClassName: PropTypes.string,
  headerClassName: PropTypes.string,
  onClose: PropTypes.func,
  onItemClick: PropTypes.func,
  onBackClick: PropTypes.func,
};

MultilevelSidebar.defaultProps = {
  persist: false,
};
